import { Schema } from "effect"
import { HttpApiEndpoint, HttpApiGroup } from "effect/http-api"
import { Authentication } from "./AuthApi.ts"
import { NoProviderForVenue, ProviderNotConnected, ProviderUnavailable, UnknownInstrument } from "./Errors.ts"
import { CandlesResponse, InstrumentId, Timeframe } from "./MarketData.ts"

/** What one sync stored for one series (docs/design/0010). */
export const SyncResult = Schema.Struct({
  instrument: InstrumentId,
  timeframe: Timeframe,
  /** Closed bars written; each resume also rewrites the last two bars already stored. */
  bars: Schema.Number,
  /** End of the stored range (epoch seconds), null when nothing new was stored. */
  to: Schema.NullOr(Schema.Number),
  /** Why this series couldn't be synced, null when it was. */
  error: Schema.NullOr(Schema.String)
})
export type SyncResult = typeof SyncResult.Type

export const SyncResponse = Schema.Struct({
  /** A sync was already running (the first one backfills years and takes a while); this request did nothing. */
  alreadyRunning: Schema.Boolean,
  results: Schema.Array(SyncResult)
})
export type SyncResponse = typeof SyncResponse.Type

export const CandlesQuery = {
  instrument: InstrumentId,
  timeframe: Timeframe,
  /** Range start, inclusive (epoch seconds). */
  from: Schema.Int,
  /** Range end, exclusive (epoch seconds). May be in the future to include the forming bar. */
  to: Schema.Int,
  /**
   * A replay's time (epoch seconds): only bars closed by then, plus the bar forming at that time as
   * built from 1m bars so far (docs/design/0011). Without it, the present.
   */
  asOf: Schema.optional(Schema.Int)
}

export class CandlesApi extends HttpApiGroup.make("candles")
  .add(
    HttpApiEndpoint.get("get", "/", {
      query: CandlesQuery,
      success: CandlesResponse,
      error: [UnknownInstrument, NoProviderForVenue, ProviderNotConnected, ProviderUnavailable]
    }),
    // Open time of the newest stored bar, from any source: lets a chart open on
    // stored history when the recent range has nothing (e.g. a dataset that ends earlier).
    HttpApiEndpoint.get("latest", "/latest", {
      query: { instrument: InstrumentId, timeframe: Timeframe },
      success: Schema.Struct({ time: Schema.NullOr(Schema.Number) }),
      error: UnknownInstrument
    }),
    // Fetches new bars for the configured sync list now (the server also syncs on a schedule).
    HttpApiEndpoint.post("sync", "/sync", { success: SyncResponse })
  )
  .middleware(Authentication)
  .prefix("/candles")
{}
